import { Component, EventEmitter, Output, computed, inject, signal } from '@angular/core';
import { SmartBroadcastService } from '../../../../../services/smart-broadcast/smart-broadcast.service';
import { ISmartBroadcastProblem } from '../../../../../types/smartBroadcast';
import { csvFileName, downloadCsv, toCsv } from '../../../../../../utils/csv';
import { formatCount } from '../../../../../../utils/sms';

type Filter = 'all' | 'number' | 'field' | 'cost';

const PAGE = 50;

/**
 * Rows worth a look before sending: numbers that won't work, fields that
 * would go out as written, and messages that cost more than most. Opening a
 * row shows its message in the preview.
 */
@Component({
  selector: 'sb-rows-to-check',
  standalone: true,
  templateUrl: './rows-to-check.component.html',
  styleUrls: ['../../sb-shared.css', './rows-to-check.component.css'],
})
export class RowsToCheckComponent {
  /** Emits the row index to show in the preview. */
  @Output() preview = new EventEmitter<number>();

  store = inject(SmartBroadcastService);

  readonly filter = signal<Filter>('all');
  readonly limit = signal(PAGE);
  readonly formatCount = formatCount;

  readonly filters: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'number', label: 'Numbers' },
    { id: 'field', label: 'Empty values' },
    { id: 'cost', label: 'Long messages' },
  ];

  readonly counts = computed<Record<Filter, number>>(() => {
    const counts: Record<Filter, number> = { all: 0, number: 0, field: 0, cost: 0 };
    for (const problem of this.store.problems()) {
      counts.all++;
      for (const kind of new Set(problem.issues.map((issue) => issue.kind))) counts[kind]++;
    }
    return counts;
  });

  readonly filtered = computed<ISmartBroadcastProblem[]>(() => {
    const filter = this.filter();
    const problems = this.store.problems();
    return filter === 'all'
      ? problems
      : problems.filter((problem) => problem.issues.some((issue) => issue.kind === filter));
  });

  readonly visible = computed(() => this.filtered().slice(0, this.limit()));
  readonly hidden = computed(() => Math.max(0, this.filtered().length - this.limit()));
  readonly nextBatch = computed(() => Math.min(PAGE, this.hidden()));

  setFilter(filter: Filter) {
    this.filter.set(filter);
    this.limit.set(PAGE);
  }

  showMore() {
    this.limit.update((limit) => limit + PAGE);
  }

  open(row: number) {
    this.preview.emit(row);
  }

  openFromKeyboard(event: Event, row: number) {
    event.preventDefault();
    this.open(row);
  }

  /** Every listed row with its original values, to fix in the sheet. */
  download() {
    const headers = this.store.headersSig();
    const rows = this.store.rowsSig();
    const columns = [
      { label: 'Sheet row', value: (p: ISmartBroadcastProblem) => p.sheetRow },
      { label: 'What we found', value: (p: ISmartBroadcastProblem) => p.issues.map((i) => i.text).join('; ') },
      { label: 'What happens', value: (p: ISmartBroadcastProblem) => p.issues.map((i) => i.outcome).join('; ') },
      ...headers.map((header, index) => ({
        label: header || `Column ${index + 1}`,
        value: (p: ISmartBroadcastProblem) => rows[p.row]?.[index] ?? '',
      })),
    ];
    downloadCsv(
      csvFileName('rows to check', this.store.subjectSig() || this.store.fileNameSig()),
      toCsv(this.filtered(), columns)
    );
  }
}
